/* ============================================================================
   CAMPO DE COORDENADA

   El campo pedía grados decimales, y la aviación no trabaja así: la AIP publica
   `020742S`, las cartas van en grados y minutos, y un piloto que lee una
   coordenada de una carta tenía que convertirla en la cabeza antes de
   escribirla.

   Una cuenta hecha a mano es un error esperando.

   ══════════════════════════════════════════════════════════════════════════
   MIENTRAS SE ESCRIBE NO SE CORRIGE NADA

   Reformatear al vuelo es lo que hace imposible escribir en estos campos: uno
   teclea `02°0` y el campo lo convierte a `02°00'00"S`, moviéndole el cursor y
   borrándole lo que venía después.

   Acá el texto queda **tal como se escribe** hasta que el campo pierde el
   foco. Recién ahí se interpreta y se reescribe en el formato de la carta.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

import React, { useEffect, useState } from "react";
import { leerCoordenada, aGms, EJE } from "../../domain/coordenadas.js";

/**
 * Campo de coordenada.
 *
 * @param {number|null} valor   en grados decimales, que es como se guarda
 * @param {string} eje          "latitud" o "longitud"
 * @param {function} alCambiar  recibe grados decimales o null
 */
export default function CampoCoordenada({
  valor,
  eje = EJE.latitud,
  alCambiar,
  placeholder = "sin definir",
  style = {},
  disabled = false,
  id,
}) {
  /* Lo que el piloto ve mientras escribe. Se separa del valor guardado porque
     son dos cosas distintas: el texto a medio escribir no es una coordenada
     todavía. */
  const [texto, setTexto] = useState(() => (
    Number.isFinite(valor) ? aGms(valor, eje) : ""
  ));
  const [escribiendo, setEscribiendo] = useState(false);
  const [error, setError] = useState(null);

  /* Si el valor cambia desde afuera —al tocar el mapa, al buscar un lugar— el
     campo se actualiza. Pero NO mientras se está escribiendo: pisarle el texto
     al piloto en medio de la carga es la forma más rápida de perder un dato. */
  useEffect(() => {
    if (escribiendo) return;
    setTexto(Number.isFinite(valor) ? aGms(valor, eje) : "");
    setError(null);
  }, [valor, eje, escribiendo]);

  const confirmar = () => {
    setEscribiendo(false);

    const crudo = texto.trim();
    if (crudo === "") {
      setError(null);
      if (alCambiar) alCambiar(null);
      return;
    }

    const r = leerCoordenada(crudo, eje);

    if (r.valor === null) {
      /* El texto NO se borra: borrarlo obligaría a escribir todo de nuevo, y
         casi siempre el error es un dígito. Se deja para corregir. */
      setError(r.motivo);
      return;
    }

    setError(null);
    setTexto(aGms(r.valor, eje));
    if (alCambiar) alCambiar(r.valor);
  };

  return (
    <>
      <input
        id={id}
        type="text"
        inputMode="text"
        spellCheck={false}
        autoComplete="off"
        disabled={disabled}
        value={texto}
        placeholder={placeholder}
        style={{ ...style, ...(error ? { borderColor: "#E4573D" } : null) }}
        onChange={(e) => { setTexto(e.target.value); setEscribiendo(true); setError(null); }}
        onFocus={() => setEscribiendo(true)}
        onBlur={confirmar}
        onKeyDown={(e) => {
          /* Enter confirma sin salir del campo: es lo que uno hace al terminar
             de escribir una coordenada. */
          if (e.key === "Enter") { e.currentTarget.blur(); }
          /* Escape devuelve lo que había: un error de tipeo no debería costar
             la coordenada anterior. */
          if (e.key === "Escape") {
            setTexto(Number.isFinite(valor) ? aGms(valor, eje) : "");
            setError(null);
            setEscribiendo(false);
          }
        }}
      />

      {error && (
        <span className="campo-coord__error" role="alert">{error}</span>
      )}

      {/* El decimal debajo, chico: es lo que va en una exportación o en un
          equipo que lo pida, y tenerlo a la vista evita ir a convertirlo. */}
      {!error && Number.isFinite(valor) && (
        <span className="campo-coord__decimal">{valor.toFixed(5)}°</span>
      )}
    </>
  );
}
